/**
 * DIFF-2 seed — Dispatch Autonomy rules + sample log showing all 3 tiers.
 */
import { DEMO_TENANT_ID } from './tenantDefaults';

const TENANT = DEMO_TENANT_ID;
const iso = (days = 0, hours = 0, minutes = 0) =>
  new Date(Date.now() - (days * 86400000 + hours * 3600000 + minutes * 60000)).toISOString();

export const SEED_DISPATCH_AUTONOMY_RULES = [
  // Tier 1 — auto-dispatch for small LTL Midwest loads with preferred carriers
  {
    id: 'dar-0001', tenantId: TENANT,
    name: 'Midwest LTL — Auto Dispatch',
    tier: 1, priority: 100, active: true,
    mode: 'LTL', originState: 'IL', destState: 'IN',
    minWeight: 500, maxWeight: 10000,
    tenderPreferences: { preferredCarrierIds: ['carrier-od', 'carrier-saia'], equipmentMatch: true },
    description: 'Small LTL on IL→IN with OD or Saia — historically 98% on-time, safe for full auto.',
    isDeleted: false, createdAt: iso(30), updatedAt: iso(2), version: 2, source: 'admin',
  },
  // Tier 1 — auto-dispatch for Pacific Fresh OTR
  {
    id: 'dar-0002', tenantId: TENANT,
    name: 'Pacific Fresh OTR — Auto Dispatch',
    tier: 1, priority: 95, active: true,
    mode: 'OTR', customerId: 'cust-pacific-fresh',
    minWeight: 30000, maxWeight: 44500,
    tenderPreferences: { preferredCarrierIds: ['carrier-xpo', 'carrier-od'] },
    description: 'Pacific Fresh full-truck — EDI-integrated customer, rates pre-negotiated.',
    isDeleted: false, createdAt: iso(45), updatedAt: iso(5), version: 1, source: 'admin',
  },
  // Tier 2 — recommendation for large TX→CA
  {
    id: 'dar-0003', tenantId: TENANT,
    name: 'Long-Haul TX→CA — One-Click',
    tier: 2, priority: 80, active: true,
    mode: 'OTR', originState: 'TX', destState: 'CA',
    minWeight: 20000,
    description: 'Long lane + higher rate — dispatcher must one-click approve before tender fires.',
    isDeleted: false, createdAt: iso(60), updatedAt: iso(10), version: 1, source: 'admin',
  },
  // Tier 2 — recommendation for Heartland Grain
  {
    id: 'dar-0004', tenantId: TENANT,
    name: 'Heartland Grain — One-Click',
    tier: 2, priority: 75, active: true,
    customerId: 'cust-heartland-grain',
    description: 'Heartland runs seasonal — dispatcher approves carrier to maintain relationship.',
    isDeleted: false, createdAt: iso(50), updatedAt: iso(7), version: 1, source: 'admin',
  },
  // Tier 3 — assisted for intermodal
  {
    id: 'dar-0005', tenantId: TENANT,
    name: 'Intermodal — Assisted',
    tier: 3, priority: 40, active: true,
    mode: 'INTERMODAL',
    description: 'Ramp availability varies — always surface context for the dispatcher.',
    isDeleted: false, createdAt: iso(90), updatedAt: iso(20), version: 1, source: 'admin',
  },
];

export const SEED_AGENT_DISPATCH_LOG = [
  // Tier 1 — auto-dispatched
  {
    id: 'adl-0001', tenantId: TENANT,
    loadId: 'load-00060', loadNumber: 'WC-10760',
    tier: 1, ruleId: 'dar-0001', ruleName: 'Midwest LTL — Auto Dispatch',
    decision: 'auto_dispatched',
    reasoning: 'ODFL preferred carrier, 98% on-time on IL→IN over 47 prior loads. Rate match within 2% of contract.',
    carrierId: 'carrier-od', carrierName: 'Old Dominion Freight Line',
    savingsEstimate: null,
    cascadeRiskCount: 0, cascadeRiskLoadIds: [],
    decidedAt: iso(0, 0, 12), decidedBy: 'aegis',
    isDeleted: false, createdAt: iso(0, 0, 12), updatedAt: iso(0, 0, 12), version: 1, source: 'dispatch_autonomy',
  },
  {
    id: 'adl-0002', tenantId: TENANT,
    loadId: 'load-00061', loadNumber: 'WC-10761',
    tier: 1, ruleId: 'dar-0002', ruleName: 'Pacific Fresh OTR — Auto Dispatch',
    decision: 'auto_dispatched',
    reasoning: 'XPO preferred carrier for Pacific Fresh. Equipment type 53 ft dry matches. EDI 204 acknowledged.',
    carrierId: 'carrier-xpo', carrierName: 'XPO Logistics',
    cascadeRiskCount: 0, cascadeRiskLoadIds: [],
    decidedAt: iso(0, 1, 5), decidedBy: 'aegis',
    isDeleted: false, createdAt: iso(0, 1, 5), updatedAt: iso(0, 1, 5), version: 1, source: 'dispatch_autonomy',
  },
  // Tier 2 — recommendation awaiting approval
  {
    id: 'adl-0003', tenantId: TENANT,
    loadId: 'load-00062', loadNumber: 'WC-10762',
    tier: 2, ruleId: 'dar-0003', ruleName: 'Long-Haul TX→CA — One-Click',
    decision: 'recommended',
    reasoning: 'USA Truck best match — 94% on-time over 12 prior runs on TX→CA, carrier hungry (empty trailer in Dallas).',
    carrierId: 'carrier-usat', carrierName: 'USA Truck',
    cascadeRiskCount: 0, cascadeRiskLoadIds: [],
    decidedAt: iso(0, 0, 45), decidedBy: 'aegis',
    isDeleted: false, createdAt: iso(0, 0, 45), updatedAt: iso(0, 0, 45), version: 1, source: 'dispatch_autonomy',
  },
  {
    id: 'adl-0004', tenantId: TENANT,
    loadId: 'load-00063', loadNumber: 'WC-10763',
    tier: 2, ruleId: 'dar-0004', ruleName: 'Heartland Grain — One-Click',
    decision: 'approved',
    reasoning: 'Approved by dispatcher — Schneider chosen over recommendation (relationship reasons).',
    carrierId: 'carrier-schneider', carrierName: 'Schneider',
    cascadeRiskCount: 0, cascadeRiskLoadIds: [],
    decidedAt: iso(0, 2, 10), decidedBy: 'aegis',
    isDeleted: false, createdAt: iso(0, 2, 10), updatedAt: iso(0, 2, 10), version: 1, source: 'dispatch_autonomy',
  },
  // Tier 3 — suggestion only
  {
    id: 'adl-0005', tenantId: TENANT,
    loadId: 'load-00064', loadNumber: 'WC-10764',
    tier: 3, ruleId: 'dar-0005', ruleName: 'Intermodal — Assisted',
    decision: 'suggested',
    reasoning: 'J.B. Hunt intermodal — ramp at Willow Springs has 6 slots open. Alt: Schneider rail via Chicago.',
    carrierId: 'carrier-jbhunt', carrierName: 'J.B. Hunt',
    cascadeRiskCount: 0, cascadeRiskLoadIds: [],
    decidedAt: iso(0, 3, 20), decidedBy: 'aegis',
    isDeleted: false, createdAt: iso(0, 3, 20), updatedAt: iso(0, 3, 20), version: 1, source: 'dispatch_autonomy',
  },
  // Cascade flag — delayed load impacting downstream
  {
    id: 'adl-0006', tenantId: TENANT,
    loadId: 'load-00065', loadNumber: 'WC-10765',
    tier: 3, ruleId: null, ruleName: 'Cascade detector',
    decision: 'cascade_flag',
    reasoning: 'Delay on WC-10765 threatens 3 downstream load(s): same carrier/driver pickup in next 24h.',
    carrierId: 'carrier-swft', carrierName: 'Swift Transportation',
    cascadeRiskCount: 3, cascadeRiskLoadIds: ['load-00070', 'load-00071', 'load-00072'],
    decidedAt: iso(0, 0, 30), decidedBy: 'aegis',
    isDeleted: false, createdAt: iso(0, 0, 30), updatedAt: iso(0, 0, 30), version: 1, source: 'dispatch_autonomy',
  },
  // A couple older entries for realism
  {
    id: 'adl-0007', tenantId: TENANT,
    loadId: 'load-00066', loadNumber: 'WC-10766',
    tier: 1, ruleId: 'dar-0001', ruleName: 'Midwest LTL — Auto Dispatch',
    decision: 'auto_dispatched',
    reasoning: 'Saia preferred carrier on IL→IN, contract rate match.',
    carrierId: 'carrier-saia', carrierName: 'Saia LTL Freight',
    cascadeRiskCount: 0, cascadeRiskLoadIds: [],
    decidedAt: iso(1, 2), decidedBy: 'aegis',
    isDeleted: false, createdAt: iso(1, 2), updatedAt: iso(1, 2), version: 1, source: 'dispatch_autonomy',
  },
  {
    id: 'adl-0008', tenantId: TENANT,
    loadId: 'load-00067', loadNumber: 'WC-10767',
    tier: 1, ruleId: 'dar-0002', ruleName: 'Pacific Fresh OTR — Auto Dispatch',
    decision: 'skipped',
    reasoning: 'No preferred carrier available for requested pickup window. Escalating to Tier 2.',
    carrierId: null, carrierName: null,
    cascadeRiskCount: 0, cascadeRiskLoadIds: [],
    decidedAt: iso(1, 8), decidedBy: 'aegis',
    isDeleted: false, createdAt: iso(1, 8), updatedAt: iso(1, 8), version: 1, source: 'dispatch_autonomy',
  },
];

export const DISPATCH_AUTONOMY_SEED_SUMMARY = {
  dispatch_autonomy_rules: SEED_DISPATCH_AUTONOMY_RULES.length,
  agent_dispatch_log: SEED_AGENT_DISPATCH_LOG.length,
};
